import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';

import { StudiotelConfigFile } from '../../stepper.component';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'flex-start',
    flex: 1,
    maxHeight: `calc(100vh - ${350}px)`,
    overflow: 'auto',
  },
  formContent: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(2),
    width: 380,
  },
}));

const SOUND_SETTINGS = { min: 1700, max: 1799 };

interface IProps {
  configFile: StudiotelConfigFile;
  setConfigFile: React.Dispatch<React.SetStateAction<StudiotelConfigFile>>;
  deviceIndex?: number;
  useDevices?: boolean;
}

export default function Form(props: IProps) {
  const { configFile, setConfigFile, deviceIndex = 0, useDevices = false } = props;
  const classes = useStyles();
  const [errors, setErrors] = React.useState({
    nb_pre: false,
    nb_att: false,
    nb_rep: false,
  });

  // Lecture depuis devices[deviceIndex] si redondant, sinon depuis les champs plats
  const currentDevice = useDevices
    ? (configFile?.studiotel?.devices?.[deviceIndex] || {})
    : configFile?.studiotel;

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;

    if (useDevices) {
      const currentDevices = [...(configFile.studiotel.devices || [])];
      currentDevices[deviceIndex] = { ...(currentDevices[deviceIndex] || {}), [name]: value };
      setConfigFile({
        ...configFile,
        studiotel: { ...configFile.studiotel, devices: currentDevices },
      });
    } else {
      setConfigFile({
        ...configFile,
        studiotel: { ...configFile.studiotel, [name]: value },
      });
    }
  }

  function handleChangeToNumber(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value, min, max } = event.target;
    const parsedValue = parseInt(value) ?? 0;
    const isError = parsedValue < Number(min) || parsedValue > Number(max);

    setErrors((errors) => ({ ...errors, [name]: isError }));

    if (useDevices) {
      const currentDevices = [...(configFile.studiotel.devices || [])];
      currentDevices[deviceIndex] = { ...(currentDevices[deviceIndex] || {}), [name]: parsedValue };
      setConfigFile({
        ...configFile,
        studiotel: { ...configFile.studiotel, devices: currentDevices },
      });
    } else {
      setConfigFile({
        ...configFile,
        studiotel: { ...configFile.studiotel, [name]: parsedValue },
      });
    }
  }

  return (
    <div className={classes.formContent}>
      <TextField
        name="hostname"
        label="Nom de domaine ou IP"
        type="text"
        variant="outlined"
        size="small"
        margin="normal"
        autoComplete="off"
        value={currentDevice.hostname || ''}
        onChange={handleChange}
        helperText="Veuillez entrer l'adresse IP ou le nom de domaine de l'interface OXE. Par exemple : 192.168.1.10 ou oxe.local.lan"
      />
      <TextField
        name="port"
        label="Port"
        type="number"
        variant="outlined"
        size="small"
        margin="normal"
        autoComplete="off"
        value={currentDevice.port || ''}
        onChange={handleChangeToNumber}
        helperText="Le numéro de port SSH. Pour information, le port par défaut du SSH est le numéro 22"
      />
      <TextField
        name="login"
        label="Login"
        type="text"
        variant="outlined"
        size="small"
        margin="normal"
        autoComplete="new-login"
        value={currentDevice.login || ''}
        onChange={handleChange}
      />
      <TextField
        name="password"
        label="Mot de passe"
        type="password"
        variant="outlined"
        size="small"
        margin="normal"
        autoComplete="new-password"
        value={currentDevice.password || ''}
        onChange={handleChange}
      />
      <TextField
        name="nb_pre"
        label="Numéro du son pré-décroché"
        type="number"
        variant="outlined"
        size="small"
        margin="normal"
        autoComplete="off"
        helperText="Veuillez entrer l'identifiant du champ pour le son pré-décroché (de 1700 à 1799 inclus) qui a été déclaré sur l'interface web lors de la configuration du matériel"
        value={currentDevice.nb_pre || ''}
        onChange={handleChangeToNumber}
        error={errors.nb_pre}
        inputProps={SOUND_SETTINGS}
      />
      <TextField
        name="nb_att"
        label="Numéro du son d'attente"
        type="number"
        variant="outlined"
        size="small"
        margin="normal"
        autoComplete="off"
        helperText="Veuillez entrer l'identifiant du champ pour le son d'attente (de 1700 à 1799 inclus) qui a été déclaré sur l'interface web lors de la configuration du matériel"
        value={currentDevice.nb_att || ''}
        onChange={handleChangeToNumber}
        error={errors.nb_att}
        inputProps={SOUND_SETTINGS}
      />
      <TextField
        name="nb_rep"
        label="Numéro du son de répondeur"
        type="number"
        variant="outlined"
        size="small"
        margin="normal"
        autoComplete="off"
        helperText="Veuillez entrer l'identifiant du champ pour le son répondeur (de 1700 à 1799 inclus) qui a été déclaré sur l'interface web lors de la configuration du matériel"
        value={currentDevice.nb_rep || ''}
        onChange={handleChangeToNumber}
        error={errors.nb_rep}
        inputProps={SOUND_SETTINGS}
      />
    </div>
  );
}
